import { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { Star, X, Send, ExternalLink, Heart } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n.jsx";
import { markPrompted, markSubmitted } from "@/lib/reviewPrompt";

/**
 * ReviewPromptModal — 5-star sheet shown after a successful meal save.
 *
 * Flow:
 *   • Mounts with 5 EMPTY stars — user taps to fill 1..5.
 *   • Rating changes are instant preview (with playful "Almost!" copy
 *     to nudge toward 5 stars without being manipulative).
 *   • On "موافق":
 *       – rating === 5 → POST to /reviews with `redirected_to_store: true`
 *         then open the App Store / Play Store review page. Never asks
 *         this user again.
 *       – rating ≤  4 → show the free-text feedback field. Submit sends
 *         only to our DB (never to the public store). Sets a 14-day
 *         cooldown before we ask again.
 *   • Dismiss (X or backdrop): still counts as a prompt so the
 *     cooldown starts, but no rating is saved.
 *
 * Store URLs live inline — the iOS one is a placeholder using the
 * Calofay marketing slug and expects the App Store team ID to be
 * appended once we know the App Store Connect record for TestFlight
 * vs Production. Android's package name is stable (`com.evanacare.fitcal`).
 */

const APPLE_APP_ID     = "6755807048"; // Calofay on the App Store (LIVE)
const ANDROID_PACKAGE  = "com.calofay.app"; // matches android/app/build.gradle applicationId

function nativePlatform() {
  try { return Capacitor.getPlatform?.() || "web"; } catch { return "web"; }
}

// Must run synchronously inside the tap handler. On iOS native, window.open is
// handed to UIApplication.open (Capacitor) which launches the App Store app;
// a location.href navigation gets cancelled by the router push that follows.
function openStoreForReview() {
  const p = nativePlatform();
  const isAndroid = p === "android" || (p === "web" && /Android/i.test(navigator.userAgent || ""));
  if (p === "android") {
    window.location.href = `market://details?id=${ANDROID_PACKAGE}`;
    return;
  }
  const url = isAndroid
    ? `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`
    : `https://apps.apple.com/app/id${APPLE_APP_ID}?action=write-review`;
  const w = window.open(url, "_blank");
  if (!w && p === "web") window.location.href = url;
}

function storeLabel(en) {
  const p = nativePlatform();
  const isAndroid = p === "android" || (p === "web" && /Android/i.test(navigator.userAgent || ""));
  if (isAndroid) return "Google Play";
  return en ? "the App Store" : "متجر أبل";
}

export default function ReviewPromptModal({ open, onClose }) {
  const { lang } = useI18n();
  const en = lang === "en";
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [step, setStep] = useState("rate"); // rate | feedback | thanks
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      markPrompted();
      setRating(0); setHovered(0); setFeedback(""); setStep("rate");
    }
  }, [open]);

  if (!open) return null;

  const storeName = storeLabel(en);

  const submit5Stars = () => {
    openStoreForReview();
    markSubmitted(5);
    api.post("/reviews", {
      rating: 5,
      platform: nativePlatform(),
      redirected_to_store: true,
    }).catch(() => {});
    toast.success(en ? "Thank you! 🙌" : "الله يسعدك، مشكور على دعمك 🙌");
    setTimeout(onClose, 400);
  };

  const submitLowRating = async () => {
    const clean = feedback.trim();
    if (!clean) {
      toast.error(en ? "Please tell us what went wrong" : "قولنا وش الطاري عشان نقدر نساعدك");
      return;
    }
    setBusy(true);
    try {
      await api.post("/reviews", {
        rating,
        feedback: clean,
        platform: nativePlatform(),
        redirected_to_store: false,
      });
      markSubmitted(rating);
      setStep("thanks");
    } catch {
      toast.error(en ? "Couldn't send — try again" : "ما قدرنا نرسل، جرّب مرة ثانية");
    } finally { setBusy(false); }
  };

  const proceed = () => {
    if (rating === 0) {
      toast.error(en ? "Please pick a rating" : "لا تنسى تحدد النجوم أول شي");
      return;
    }
    if (rating === 5) setStep("ask");
    else setStep("feedback");
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="review-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-200"
        dir={en ? "ltr" : "rtl"}
        data-testid="review-modal"
      >
        {/* Header */}
        <div className="relative px-5 pt-5 pb-3">
          <button
            onClick={onClose}
            className="absolute top-3 left-3 rtl:right-3 rtl:left-auto w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center hover:bg-neutral-200 transition"
            data-testid="review-close-btn"
            aria-label="close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === "rate" && (
          <div className="px-6 pb-6 space-y-5 text-center">
            <div className="w-16 h-16 mx-auto rounded-3xl orange-gradient text-white flex items-center justify-center shadow-brand">
              <Heart className="w-8 h-8" fill="currentColor" />
            </div>
            <div>
              <h2 className="text-xl font-black text-neutral-900">
                {en ? "Enjoying Calofay?" : "رأيك بكالوفاي"}
              </h2>
              <p className="text-sm text-neutral-500 leading-relaxed mt-2 max-w-xs mx-auto">
                {en
                  ? "Your rating helps other people discover the app — it takes 5 seconds and makes a huge difference."
                  : "تقييمك يفرق معنا كثييير عشان تطبيقنا يستمر ونطوّر التطبيق 🌹"}
              </p>
            </div>

            {/* Stars */}
            <div
              className="flex items-center justify-center gap-2 py-3"
              onMouseLeave={() => setHovered(0)}
              data-testid="review-stars"
            >
              {[1, 2, 3, 4, 5].map((n) => {
                const active = (hovered || rating) >= n;
                return (
                  <button
                    key={n}
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHovered(n)}
                    className="p-1 transition-transform hover:scale-110 active:scale-95"
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    data-testid={`review-star-${n}`}
                  >
                    <Star
                      className={`w-10 h-10 transition-colors ${
                        active
                          ? "text-amber-400 drop-shadow-[0_2px_6px_rgba(251,191,36,0.4)]"
                          : "text-neutral-200"
                      }`}
                      fill={active ? "currentColor" : "none"}
                      strokeWidth={1.75}
                    />
                  </button>
                );
              })}
            </div>

            {rating > 0 && (
              <div
                className={`text-sm font-bold h-5 ${
                  rating === 5 ? "text-emerald-600" : rating >= 3 ? "text-amber-600" : "text-rose-600"
                }`}
                data-testid="review-rating-label"
              >
                {rating === 5 && (en ? "Amazing! 🎉" : "الله يسعدك 🎉")}
                {rating === 4 && (en ? "Great — how can we get to 5?" : "قرّبنا للـ ٥ — قولنا وش نحسّن؟")}
                {rating === 3 && (en ? "Thanks for the honesty" : "تسلم على صراحتك")}
                {rating <= 2 && (en ? "We're sorry — help us do better" : "آسفين، وش ينقصنا عشان نطوّره؟")}
              </div>
            )}

            {rating === 5 && (
              <div
                className="text-[12px] text-neutral-500 bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-2.5 leading-relaxed"
                data-testid="review-store-hint"
              >
                {en
                  ? "We'll take you to the App Store to leave your rating there 🌹"
                  : "بنوّديك للمتجر عشان تحط تقييمك هناك ويوصل لباقي الناس 🌹"}
              </div>
            )}

            <button
              onClick={proceed}
              disabled={busy || rating === 0}
              className="w-full orange-gradient text-white font-black py-4 rounded-full flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-40 shadow-brand"
              data-testid="review-submit-btn"
            >
              <Send className="w-4 h-4" />
              <span>
                {en ? "OK" : "تمام"}
              </span>
            </button>
          </div>
        )}

        {step === "ask" && (
          <div className="px-6 pb-6 space-y-5 text-center" data-testid="review-ask-step">
            <div className="w-16 h-16 mx-auto rounded-3xl orange-gradient text-white flex items-center justify-center shadow-brand">
              <Heart className="w-8 h-8" fill="currentColor" />
            </div>
            <div className="flex justify-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className="w-6 h-6 text-amber-400" fill="currentColor" strokeWidth={1.75} />
              ))}
            </div>
            <div>
              <h2 className="text-xl font-black text-neutral-900">
                {en ? "A small favor 🤍" : "عندنا طلب صغير 🤍"}
              </h2>
              <p className="text-sm text-neutral-600 leading-relaxed mt-2 max-w-xs mx-auto" data-testid="review-ask-text">
                {en
                  ? `Would you write us a short review and rate us 5 stars on ${storeName}? It would help us a lot and truly support us to keep going and keep improving Calofay 🌹`
                  : `ممكن تكتب لنا تقييم بسيط وتقيّمنا بخمس نجوم في ${storeName}؟ راح يساعدنا كثييير وراح تدعمنا عشان نستمر ونطوّر كالوفاي 🌹`}
              </p>
            </div>
            <button
              onClick={submit5Stars}
              disabled={busy}
              className="w-full orange-gradient text-white font-black py-4 rounded-full flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-40 shadow-brand"
              data-testid="review-ask-yes-btn"
            >
              <ExternalLink className="w-4 h-4" />
              <span>{en ? `Yes, I'd love to support you on ${storeName}` : `نعم أرغب بدعمكم والتقييم في ${storeName}`}</span>
            </button>
            <button
              onClick={onClose}
              className="w-full text-sm text-neutral-500 hover:text-neutral-800 py-2"
              data-testid="review-ask-later-btn"
            >
              {en ? "Maybe later" : "لاحقاً"}
            </button>
          </div>
        )}

        {step === "feedback" && (
          <div className="px-6 pb-6 space-y-4">
            <div className="text-center">
              <h2 className="text-lg font-black text-neutral-900">
                {en ? "What could be better?" : "وش اللي ما عجبك؟"}
              </h2>
            </div>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={en ? "Tell us what happened, what you'd love to see..." : "أكتب لنا وش صار أو وش تبينا نسوّي أحسن..."}
              rows={5}
              className="w-full text-sm bg-neutral-50 border-2 border-neutral-200 rounded-2xl px-4 py-3 focus:outline-none focus:border-brand-400 resize-none placeholder:text-neutral-400 leading-relaxed"
              data-testid="review-feedback-textarea"
              autoFocus
            />
            <button
              onClick={submitLowRating}
              disabled={busy || !feedback.trim()}
              className="w-full orange-gradient text-white font-black py-4 rounded-full flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-40 shadow-brand"
              data-testid="review-feedback-submit-btn"
            >
              <Send className="w-4 h-4" />
              <span>{en ? "Send feedback" : "أرسل"}</span>
            </button>
            <button
              onClick={() => setStep("rate")}
              className="w-full text-sm text-neutral-500 hover:text-neutral-800 py-2"
            >
              {en ? "← Back" : "→ رجوع"}
            </button>
          </div>
        )}

        {step === "thanks" && (
          <div className="px-6 pb-8 pt-2 space-y-4 text-center">
            <div className="w-16 h-16 mx-auto rounded-3xl bg-emerald-500 text-white flex items-center justify-center shadow-lg">
              <Heart className="w-8 h-8" fill="currentColor" />
            </div>
            <div>
              <h2 className="text-xl font-black text-neutral-900">
                {en ? "Thank you 🙏" : "الله يعافيك 🙏"}
              </h2>
              <p className="text-sm text-neutral-500 leading-relaxed mt-2 max-w-xs mx-auto">
                {en
                  ? "Your feedback was sent directly to our team. We read every message."
                  : "وصلتنا ملاحظتك، وعد منا نقراها ونشتغل عليها عشان كالوفاي يصير أحسن لك 🤍"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-full bg-neutral-100 text-neutral-800 font-bold py-3.5 rounded-full active:scale-[0.98]"
              data-testid="review-thanks-close-btn"
            >
              {en ? "Close" : "تمام"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
